import { Button, IconButton, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import config from 'config';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, boxShadows, palette, spaces } from '../../../../assets/styles/themes/tokens';
import { sharingLinks } from '../../../../constants';
import services from '../../../../services';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';
import {
	downloadHtmlAsDocx,
	downloadHtmlAsPdf,
	printHtmlDocument,
} from '../../../../utils/documentExport';
import getMuiIcon from '../../../../utils/getMuiIcon';
import ShareDialog from '../../../lernsets/ShareDialog';
import { convertTipTapHtmlForPdf } from '../../../utils/contentConverter';
import { AIAssistantAlerts } from '../AIAssistantAlerts';
import { sanitizeFilename } from '../utils';

export type TextEditorDownloadFormat = 'pdf' | 'docx';

interface AIAssistantTextEditorExportButtonProps {
	buttonType?: 'icon' | 'button';
	showNotification?: boolean;
	menuAnchorOrigin?: 'left' | 'right';
	showShare?: boolean;
	id?: string;
	label?: string;
	variant?: 'text' | 'outlined' | 'contained';
	dataTestId?: string;
	content?: string;
	onDownload?: (format: TextEditorDownloadFormat) => void;
	onPrint?: () => void;
	onShare?: () => void;
}

const AIAssistantTextEditorExportButton: React.FC<AIAssistantTextEditorExportButtonProps> = ({
	buttonType = 'button',
	showNotification = true,
	menuAnchorOrigin = 'left',
	showShare = true,
	id,
	label,
	variant = 'contained',
	dataTestId = 'text-editor-export-button',
	content: contentOverride,
	onDownload,
	onPrint,
	onShare,
}) => {
	const { t } = useTranslation();

	const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
	const [isPublicLinkDialogOpen, setIsPublicLinkDialogOpen] = useState(false);
	const [publicLink, setPublicLink] = useState('');
	const [isCreatingPublicLink, setIsCreatingPublicLink] = useState(false);
	const isEnableAiAssistantTextEditorExportButton = config.ai.textEditorExportButton.enabled;
	const { editorData } = useContext(aiAssistantEditorContext);
	const { setDocumentDownloadAlertVisible } = useContext(aiAssistantContext);
	const content = contentOverride ?? editorData.content;
	const title = editorData.title;
	const menuIconSx = {
		fill: palette.text.primary,
		color: palette.text.primary,
		marginRight: spaces.md,
	};
	const handleExportMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setExportMenuAnchor(event.currentTarget);
	};

	const handleExportMenuClose = () => {
		setExportMenuAnchor(null);
	};

	const handleDownloadPdf = async () => {
		handleExportMenuClose();
		if (showNotification) {
			setDocumentDownloadAlertVisible(true);
		}

		try {
			const sanitizedTitle = sanitizeFilename(title);
			const pdfReadyHtml = convertTipTapHtmlForPdf(content);
			await downloadHtmlAsPdf(pdfReadyHtml, sanitizedTitle);
			onDownload?.('pdf');
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	const handleDownloadDocx = async () => {
		handleExportMenuClose();
		if (showNotification) {
			setDocumentDownloadAlertVisible(true);
		}

		try {
			const sanitizedTitle = sanitizeFilename(title);
			const pdfReadyHtml = convertTipTapHtmlForPdf(content);
			await downloadHtmlAsDocx(pdfReadyHtml, sanitizedTitle);
			onDownload?.('docx');
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	const handlePrint = () => {
		handleExportMenuClose();
		try {
			const pdfReadyHtml = convertTipTapHtmlForPdf(content);
			printHtmlDocument(title, pdfReadyHtml);
			// Track after print dialog opens
			setTimeout(() => {
				onPrint?.();
			}, 250);
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	const handleShare = async () => {
		handleExportMenuClose();
		if (!editorData.id) {
			// Cannot share documents without an ID (playground mode)
			return;
		}

		setIsCreatingPublicLink(true);
		try {
			const { accessToken } = await services.ai.createPublicDocumentLink(editorData.id);
			const link = `${sharingLinks.generatedDocument}/${accessToken}`;
			setPublicLink(link);
			setIsPublicLinkDialogOpen(true);
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
		setIsCreatingPublicLink(false);
		onShare?.();
	};

	const renderShareDialog = () => {
		return (
			<ShareDialog
				title={t('Share this document with your students')}
				subTitle={t('This link is available for {{days}} days', { days: 60 })}
				linkUrl={publicLink}
				qrImageName={editorData.title}
				onClose={() => setIsPublicLinkDialogOpen(false)}
				open={isPublicLinkDialogOpen}
				onQrDownload={() => {}}
				onLinkCopy={() => {}}
				sx={{ zIndex: 1500 }}
			/>
		);
	};

	return (
		<>
			{showNotification && <AIAssistantAlerts showTextEditorAlertsOnly={true} />}
			{showShare && renderShareDialog()}
			{buttonType === 'icon' ? (
				<IconButton
					id={id}
					data-testid="text-editor-mobile-menu-button"
					onClick={handleExportMenuClick}
					sx={{
						padding: spaces.xs,
					}}
				>
					<SvgIcon
						component={getMuiIcon('exportIcon')}
						sx={{
							width: 24,
							height: 24,
							fill: palette.text.primary,
						}}
					/>
				</IconButton>
			) : (
				<Button
					id={id}
					data-testid={dataTestId}
					variant={variant}
					color="primary"
					size="small"
					startIcon={
						<SvgIcon
							sx={{ width: 20, height: 20 }}
							viewBox="0 0 20 20"
							component={getMuiIcon('exportIcon')}
						/>
					}
					onClick={handleExportMenuClick}
				>
					{label ?? t('Export')}
				</Button>
			)}
			<Menu
				anchorEl={exportMenuAnchor}
				open={Boolean(exportMenuAnchor)}
				onClose={handleExportMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: menuAnchorOrigin,
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: menuAnchorOrigin,
				}}
				sx={{
					zIndex: 1500,
					'& .MuiMenu-paper': {
						borderRadius: borderRadius.md,
						boxShadow: boxShadows.elevation4,
						padding: `0 ${spaces.xs}`,
					},
				}}
			>
				{isEnableAiAssistantTextEditorExportButton && (
					<MenuItem
						data-testid="menu-item-download-pdf"
						onClick={handleDownloadPdf}
						sx={{
							borderRadius: borderRadius.sm,
						}}
					>
						<SvgIcon component={getMuiIcon('downloadAsPdf')} sx={menuIconSx} />
						<Typography>{t('Download as PDF')}</Typography>
					</MenuItem>
				)}
				{isEnableAiAssistantTextEditorExportButton && (
					<MenuItem
						data-testid="menu-item-download-docx"
						onClick={handleDownloadDocx}
						sx={{
							borderRadius: borderRadius.sm,
						}}
					>
						<SvgIcon component={getMuiIcon('downloadAsDocx')} sx={menuIconSx} />
						<Typography>{t('Download as .docx')}</Typography>
					</MenuItem>
				)}
				{showShare && editorData.id && (
					<MenuItem
						data-testid="menu-item-share"
						onClick={handleShare}
						disabled={isCreatingPublicLink}
						sx={{
							borderRadius: borderRadius.sm,
						}}
					>
						<SvgIcon component={getMuiIcon('share')} sx={menuIconSx} />
						<Typography>{t('Share')}</Typography>
					</MenuItem>
				)}
				<MenuItem
					data-testid="menu-item-print"
					onClick={handlePrint}
					sx={{
						borderRadius: borderRadius.sm,
					}}
				>
					<SvgIcon
						component={getMuiIcon('printer')}
						sx={{ fill: palette.text.primary, marginRight: spaces.md }}
					/>
					<Typography>{t('Print')}</Typography>
				</MenuItem>
			</Menu>
		</>
	);
};

export default AIAssistantTextEditorExportButton;
